import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Shield, CheckCircle, LogOut, AlertCircle, FileText, Users } from 'lucide-react'
import Modal from './Modal'
import { useDataConsentModal } from '@/stores/dataConsentStore'
import { useAuth } from '@/stores/authStore'
import { toast } from '@/stores/toastStore'
import { acceptConsent } from '@/services/consentService'

const CONSENT_VERSION = '1.0' // Current consent version

export default function DataConsentModal() {
  const { t } = useTranslation('auth')
  const { isOpen, hideModal } = useDataConsentModal()
  const { user, logout } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleAcceptConsent = async () => {
    if (!user) {
      return
    }

    setIsSubmitting(true)
    try {
      const response = await acceptConsent(CONSENT_VERSION)

      const updatedUser = {
        ...user,
        dataProcessingConsent: response.dataProcessingConsent,
        consentVersion: response.consentVersion,
        consentGivenAt: response.consentGivenAt
      }

      const { updateUser } = useAuth.getState()
      updateUser(updatedUser)

      toast.success(t('dataConsent.consentSuccess'))
      hideModal()
    } catch {
      toast.error(t('dataConsent.consentError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeclineConsent = () => {
    logout()
    hideModal()
    window.location.href = '/login'
  }

  // Prevent closing the modal by clicking outside or pressing escape
  const preventClose = () => {
    // Do nothing - modal cannot be closed
  }

  return (
    <Modal isOpen={isOpen} onClose={preventClose} size="lg">
      <div className="max-h-[80vh] overflow-y-auto">
        {/* Icon */}
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Shield className="w-8 h-8 text-white" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">
          {t('dataConsent.title')}
        </h2>

        {/* Message */}
        <p className="text-gray-600 mb-6 text-center">{t('dataConsent.message')}</p>

        {/* What we collect */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3 mb-3">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <h3 className="text-sm font-semibold text-gray-900">
              {t('dataConsent.whatWeCollect')}
            </h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-700 mr-8 rtl:mr-0 rtl:ml-8">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>{t('dataConsent.personalInfo')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>{t('dataConsent.profileData')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>{t('dataConsent.usageData')}</span>
            </li>
          </ul>
        </div>

        {/* How we use it */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3 mb-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <h3 className="text-sm font-semibold text-gray-900">
              {t('dataConsent.howWeUseIt')}
            </h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-700 mr-8 rtl:mr-0 rtl:ml-8">
            <li className="flex items-start gap-2">
              <span className="text-green-600 flex-shrink-0">•</span>
              <span>{t('dataConsent.createProfile')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 flex-shrink-0">•</span>
              <span>{t('dataConsent.matchRecruiters')}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 flex-shrink-0">•</span>
              <span>{t('dataConsent.improveService')}</span>
            </li>
          </ul>
        </div>

        {/* Sharing with recruiters */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3 mb-2">
            <Users className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                {t('dataConsent.sharingInfo')}
              </h3>
              <p className="text-sm text-gray-700">
                {t('dataConsent.sharingDescription')}
              </p>
            </div>
          </div>
        </div>

        {/* Your rights */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3 mb-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                {t('dataConsent.rightsInfo')}
              </h3>
              <p className="text-sm text-gray-700">
                {t('dataConsent.rightsDescription')}
              </p>
            </div>
          </div>
        </div>

        {/* Links to privacy policy and terms */}
        <div className="flex flex-col sm:flex-row gap-2 justify-center mb-6 text-sm">
          <a
            href="/privacy-policy"
            className="text-blue-600 hover:text-blue-700 font-medium transition-colors text-center"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('dataConsent.privacyPolicy')}
          </a>
          <span className="hidden sm:inline text-gray-400">|</span>
          <a
            href="/terms-of-service"
            className="text-blue-600 hover:text-blue-700 font-medium transition-colors text-center"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('dataConsent.termsOfService')}
          </a>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {/* Accept Button */}
          <button
            onClick={handleAcceptConsent}
            disabled={isSubmitting}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors shadow-md flex items-center justify-center gap-2"
          >
            <Shield className="w-5 h-5" />
            {isSubmitting ? t('common:button.loading') : t('dataConsent.acceptButton')}
          </button>

          {/* Decline Button */}
          <button
            onClick={handleDeclineConsent}
            disabled={isSubmitting}
            className="w-full px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            {t('dataConsent.declineButton')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
